<template>
  <BaseCard
    :result="{
      orgName: searchResult.secondary_organization.name,
      orgLink: searchResult.link,
      tagName: $t(`search.events.tagName`),
      tagLink: `${searchResult.link}/events`,
    }"
    class="event-card result-card"
  >
    <template #preview>
      <div class="preview-fallback">
        <div class="day">{{ getShortDay }}</div>
        <div class="month">{{ getShortMonth }}</div>
      </div>
    </template>

    <template #result-content>
      <a
        :href="`${searchResult.link}/events?=${searchResult.id}`"
        class="title"
        >{{ searchResult.title }}</a
      >
      <div
        v-if="searchResult.description"
        class="snippet"
      >
        <span v-html="searchResult.description"></span>
      </div>

      <div
        class="meta-info"
        :class="{ 'is-mobile-view': isMobileView }"
      >
        <div class="date">
          <Icon
            name="lucide:calendar"
            size="14"
          />
          {{ formattedEventDateAndTime }}
        </div>
        <div
          v-if="searchResult?.venue || searchResult?.location"
          class="location"
        >
          <Icon
            name="lucide:map-pin"
            size="14"
          />
          {{ searchResult?.venue || searchResult?.location }}
        </div>
      </div>

      <div
        v-if="!isMobileView"
        class="card-buttons"
      >
        <button class="button add-to-calendar">
          <Icon
            name="lucide:calendar-plus"
            size="14"
          />
          {{ $t('search.events.addToCalendar') }}
        </button>
        <button class="button register">
          <Icon
            name="lucide:ticket"
            size="14"
          />
          {{ $t('search.events.register') }}
        </button>
        <div
          v-if="searchResult.publish_date"
          class="dates-and-times"
        >
          <div class="published-at">{{ $t('search.forms.published') }}: {{ formattedPublishedDateAndTime }}</div>
          <div class="updated-at"></div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup>
import BaseCard from './BaseCard.vue';
import { computed } from 'vue';
import { monthDayYear } from '../../../helpers/date-utils';
import { useScreenBreakpoints } from '@/composables/useBreakpoints';
const { isMobileView } = useScreenBreakpoints();

const props = defineProps({
  searchResult: {
    type: Object,
    required: true,
  },
});

const formattedPublishedDateAndTime = computed(() => {
  return monthDayYear(props.searchResult.publish_date) || '';
});

const formattedEventDateAndTime = computed(() => {
  const formattedDate = props.searchResult.formatted_date;
  const lastFiveCharactersInDate = formattedDate.slice(formattedDate.length - 5, formattedDate.length);
  const allDayEventsWithNoTime = lastFiveCharactersInDate === ' from';
  if (allDayEventsWithNoTime) {
    return formattedDate.slice(0, -5);
  }
  return formattedDate;
});

const getShortMonth = computed(() => {
  const date = new Date(props.searchResult.event_date);
  return date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
});

const getShortDay = computed(() => {
  const date = new Date(props.searchResult.event_date);
  return date.getDate().toString();
});
</script>

<style scoped>
.event-card {
  --color-grey-50: #f7f7f7;
  --color-grey-700: #616161;
  --color-grey-950: #2d2d2d;
  --background-color: var(--color-grey-50);
  --text-color: var(--color-grey-950);

  .day {
    font-size: 1.5rem;
    font-weight: bold;
  }

  .month {
    font-size: 1rem;
    color: var(--color-grey-700);
    text-transform: uppercase;
  }
  .meta-info.is-mobile-view {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .card-buttons {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;

    .button {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0.5rem 1rem;
      cursor: pointer;
      &.add-to-calendar {
        background-color: var(--secondary-button-background);
        color: var(--secondary-button-text);
        border: var(--secondary-button-border);
        border-radius: var(--secondary-button-border-radius);
        &:hover {
          background-color: var(--secondary-button-hover);
        }
        &:focus-visible {
          outline: var(--secondary-button-focus-outline);
          outline-offset: 1px;
          border: var(--secondary-button-hover-border);
        }
      }
      &.register {
        background-color: var(--tertiary-button-background);
        color: var(--tertiary-button-text-contrast);
        border: var(--tertiary-button-border);
        border-radius: var(--tertiary-button-border-radius);
        &:hover {
          background-color: var(--tertiary-button-hover);
        }
        &:focus-visible {
          outline: var(--tertiary-button-focus-outline);
          outline-offset: 1px;
          border: var(--tertiary-button-hover-border);
        }
      }
    }
    .dates-and-times {
      display: flex;
      gap: 12px;
    }
  }
}
</style>
